// The Loans tab: switch loans on for a bank, see the loan book, configure
// loan products (terms, fees, who may borrow) and browse the game's loans.
import { useState } from "react";
import { Affix, Button, Empty, EnvBadge, ErrorNotice, Field, InfoTip, Input, Loading, Modal, Money, Panel, Segmented, Select, useToast } from "../../components/ui";
import { gameApi, type GameApi } from "../../lib/api";
import { formatMoney, parseMoney, type CurrencyLike } from "../../lib/money";
import { invalidate, useAction, useQuery } from "../../lib/query";
import { Link } from "../../lib/router";
import { useClient } from "../../lib/session";
import type { Bank, Env, Loan, LoanProduct, LoanProductInput, RepaymentFrequency, ScoreSource } from "../../lib/types";
import type { GameTabProps } from "./Game";
import { describeBands, describeFrequency, describeTerms, loanStatusBadge } from "./loanText";
import { pct } from "./Savings";

export function GameLoans({ game, env }: GameTabProps) {
  const api = gameApi(useClient(), { gameId: game.id, env });
  const banks = useQuery(`dev:banks:${game.id}:${env}`, () => api.banks());
  const [selected, setSelected] = useState<string | null>(null);
  const list = banks.data?.data ?? [];
  const bank = list.find((b) => b.id === selected) ?? list[0];

  if (banks.loading && !banks.data) return <Loading />;
  if (banks.error) return <ErrorNotice error={banks.error} onRetry={banks.reload} />;
  if (!bank) {
    return (
      <Empty title={`No ${env} banks yet`} action={<Link className="btn primary" to={`/dev/games/${game.id}/banks${env === "production" ? "?env=production" : ""}`}>Create a bank</Link>}>
        Loan products belong to a bank. Create one first.
      </Empty>
    );
  }
  return (
    <div className="stack">
      {list.length > 1 && (
        <Field label="Bank">
          <Select value={bank.id} onChange={(e) => setSelected(e.target.value)} style={{ maxWidth: 360 }}>
            {list.map((b) => <option key={b.id} value={b.id}>{b.name} ({b.bank_code})</option>)}
          </Select>
        </Field>
      )}
      <LoansSwitch api={api} gameId={game.id} env={env} bank={bank} />
      <Products key={`p-${bank.id}`} api={api} gameId={game.id} env={env} bank={bank} />
      <LoanBook key={`l-${bank.id}`} api={api} gameId={game.id} env={env} bank={bank} />
    </div>
  );
}

function LoansSwitch({ api, gameId, env, bank }: { api: GameApi; gameId: string; env: Env; bank: Bank }) {
  const toast = useToast();
  const on = bank.feature_flags.loans === true;
  const flip = useAction(() => api.updateBank(bank.id, { feature_flags: { loans: !on } }));
  const books = useQuery(`dev:books:${gameId}:${env}:${bank.id}`, () => api.books(bank.id), { refreshMs: 15_000 });
  const b = books.data;
  const cur = bank.base_currency;
  return (
    <Panel
      title={<>Loans in {bank.name} <EnvBadge env={env} /></>}
      sub="Players borrow, and instalments are collected on the game clock. The engine scores each player's credit from their money history, or uses your game's own score."
      actions={
        <Button variant={on ? undefined : "primary"} size="small" busy={flip.pending} onClick={async () => {
          if (await flip.run()) {
            invalidate(`dev:banks:${gameId}:${env}`);
            toast(on ? "Loans turned off: new loans are refused; existing ones keep collecting" : "Loans turned on");
          }
        }}>
          {on ? "Turn loans off" : "Turn loans on"}
        </Button>
      }
    >
      <ErrorNotice error={flip.error ?? books.error} />
      <p className="small muted" style={{ margin: "0 0 12px" }}>
        {on ? <><span className="badge ok">On</span> Players can take loans from this bank's products.</> : <><span className="badge bad">Off</span> Turn loans on, then add a product below.</>}
      </p>
      {b && (
        <dl className="facts">
          <dt>Lent out, still owed <InfoTip>Principal outstanding on active loans.</InfoTip></dt><dd><Money minor={b.loans_outstanding_minor ?? 0} currency={cur} /></dd>
          <dt>Interest earned</dt><dd><Money minor={b.interest_income_minor ?? 0} currency={cur} /></dd>
          <dt>Written off <InfoTip>Defaulted and forgiven loans, from the bank's loan-loss provision.</InfoTip></dt><dd><Money minor={-(b.loan_losses_minor ?? 0)} currency={cur} /></dd>
        </dl>
      )}
    </Panel>
  );
}

function Products({ api, gameId, env, bank }: { api: GameApi; gameId: string; env: Env; bank: Bank }) {
  const toast = useToast();
  const key = `dev:loan-products:${gameId}:${env}:${bank.id}`;
  const products = useQuery(key, () => api.loanProducts(bank.id));
  const [editing, setEditing] = useState<LoanProduct | "new" | null>(null);
  const rows = products.data?.data ?? [];
  const cur = bank.base_currency;
  return (
    <Panel title="Loan products" sub="Each loan follows a product: its terms, fees, and who may borrow. Loans already made keep their terms." flush
      actions={<Button variant="primary" size="small" onClick={() => setEditing("new")}>Add loan product</Button>}>
      <ErrorNotice error={products.error} onRetry={products.reload} />
      {products.loading && !products.data && <Loading />}
      {products.data && rows.length === 0 && (
        <Empty title="No loan products yet" action={<Button variant="primary" onClick={() => setEditing("new")}>Add loan product</Button>}>
          For example "Bus Finance": 4 or 12 weeks, weekly instalments, cheaper for players with a good credit score.
        </Empty>
      )}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="data">
            <thead><tr><th>Product</th><th>Terms</th><th>Who may borrow</th><th>Fees</th><th>Status</th><th /></tr></thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id}>
                  <td>
                    <div className="cell-title">{p.name}</div>
                    <div className="cell-sub"><span className="mono">{p.code}</span> · {formatMoney(p.min_principal_minor, cur)}–{formatMoney(p.max_principal_minor, cur)} · v{p.version}</div>
                  </td>
                  <td style={{ maxWidth: 220 }}>{describeTerms(p)}<div className="cell-sub">{describeFrequency(p.repayment_frequency)}, {p.interest_method}</div></td>
                  <td className="small" style={{ maxWidth: 280 }}>
                    <div>{p.score_source === "engine" ? "Engine credit score" : p.score_source === "game" ? "Your game's score" : `Blend (${pct(p.external_score_weight_bps)} your game's)`}</div>
                    {describeBands(p, cur).map((t) => <div key={t} className="cell-sub">{t}</div>)}
                    {p.max_installment_income_bps != null && <div className="cell-sub">Instalment at most {pct(p.max_installment_income_bps)} of income</div>}
                    {p.first_loan_max_minor != null && <div className="cell-sub">First loan up to {formatMoney(p.first_loan_max_minor, cur)}</div>}
                  </td>
                  <td className="small">
                    <div>{p.origination_fee_bps || p.origination_fee_fixed_minor ? `${pct(p.origination_fee_bps)}${p.origination_fee_fixed_minor ? ` + ${formatMoney(p.origination_fee_fixed_minor, cur)}` : ""} to open` : "No opening fee"}</div>
                    <div className="cell-sub">Late: {formatMoney(p.late_fee_fixed_minor, cur)}{p.late_fee_bps ? ` + ${pct(p.late_fee_bps)}` : ""} after {p.grace_days} grace day{p.grace_days === 1 ? "" : "s"}</div>
                    <div className="cell-sub">Defaults after {p.default_after_missed} missed</div>
                  </td>
                  <td><span className={`badge ${p.status === "active" ? "ok" : "bad"}`}>{p.status === "active" ? "Active" : "Inactive"}</span></td>
                  <td className="num"><Button size="small" onClick={() => setEditing(p)}>Edit</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <ProductModal api={api} bank={bank} cur={cur} product={editing === "new" ? null : editing} onClose={() => setEditing(null)}
          onSaved={(p) => { setEditing(null); invalidate(key); toast(editing === "new" ? `${p.name} added` : "Product saved"); }} />
      )}
    </Panel>
  );
}

const toCode = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").replace(/^(\d)/, "p_$1").slice(0, 63);

interface Term { days: string; rate: string }
interface BandRow { score: string; max: string; adjust: string }

function ProductModal({ api, bank, cur, product, onClose, onSaved }: {
  api: GameApi; bank: Bank; cur: CurrencyLike; product: LoanProduct | null; onClose: () => void; onSaved: (p: LoanProduct) => void;
}) {
  const major = (v: number | null | undefined) => (v == null ? "" : String(v / 10 ** cur.exponent));
  const pctIn = (bps: number | null | undefined) => (bps == null ? "" : String(bps / 100));
  const [name, setName] = useState(product?.name ?? "");
  const [minP, setMinP] = useState(major(product?.min_principal_minor ?? 1_000 * 10 ** cur.exponent));
  const [maxP, setMaxP] = useState(major(product?.max_principal_minor ?? 1_000_000 * 10 ** cur.exponent));
  const [freq, setFreq] = useState<RepaymentFrequency>(product?.repayment_frequency ?? "weekly");
  const [method, setMethod] = useState<"amortized" | "flat">(product?.interest_method ?? "amortized");
  const [terms, setTerms] = useState<Term[]>(product?.terms.map((t) => ({ days: String(t.days), rate: pctIn(t.rate_bps) })) ?? [{ days: "28", rate: "24" }, { days: "84", rate: "30" }]);
  const [origPct, setOrigPct] = useState(pctIn(product?.origination_fee_bps ?? 100));
  const [origFixed, setOrigFixed] = useState(major(product?.origination_fee_fixed_minor || null));
  const [lateFixed, setLateFixed] = useState(major(product?.late_fee_fixed_minor ?? 500 * 10 ** cur.exponent));
  const [latePct, setLatePct] = useState(pctIn(product?.late_fee_bps || null));
  const [grace, setGrace] = useState(String(product?.grace_days ?? 1));
  const [defaultAfter, setDefaultAfter] = useState(String(product?.default_after_missed ?? 3));
  const [partial, setPartial] = useState(product?.partial_collection ?? false);
  const [source, setSource] = useState<ScoreSource>(product?.score_source ?? "engine");
  const [weight, setWeight] = useState(pctIn(product?.external_score_weight_bps ?? 5000));
  const [bands, setBands] = useState<BandRow[]>(product?.score_bands.map((b) => ({ score: String(b.min_score), max: major(b.max_principal_minor), adjust: pctIn(b.rate_adjust_bps ?? 0) }))
    ?? [{ score: "0", max: "0", adjust: "0" }, { score: "40", max: "50000", adjust: "6" }, { score: "80", max: "1000000", adjust: "-3" }]);
  const [afford, setAfford] = useState(pctIn(product ? product.max_installment_income_bps : 4000));
  const [firstMax, setFirstMax] = useState(major(product ? product.first_loan_max_minor : 100_000 * 10 ** cur.exponent));
  const [maxActive, setMaxActive] = useState(String(product?.max_active_loans ?? 1));
  const [active, setActive] = useState(product ? product.status === "active" : true);

  const money = (v: string) => (v.trim() ? parseMoney(v, cur.exponent) ?? 0 : 0);
  const bpsOf = (v: string) => (v.trim() ? parseMoney(v.replace(/^-/, ""), 2) ?? 0 : 0) * (v.trim().startsWith("-") ? -1 : 1);
  const save = useAction(() => {
    // Shares of something can't pass 100%; say which field in the form's own words, not the API's.
    const shares: [string, string, string?][] = [
      ["Opening fee", origPct, `For a flat fee, put it in "+ fixed" (in ${cur.code}) and set the percentage to 0.`],
      ["Late fee: + of the instalment", latePct, `For a flat late fee, use "Late fee" (in ${cur.code}).`],
      ["Your game's share", source === "blend" ? weight : ""],
      ["Affordability", afford],
    ];
    for (const [label, v, help] of shares) {
      const n = Number(v.trim() || "0");
      if (!Number.isFinite(n) || n < 0 || n > 100) throw new Error(`${label} is a percentage, from 0 to 100%.${help ? ` ${help}` : ""}`);
    }
    const body: LoanProductInput = {
      name: name.trim(), min_principal_minor: money(minP), max_principal_minor: money(maxP), repayment_frequency: freq, interest_method: method,
      terms: terms.map((t) => ({ days: Number(t.days), rate_bps: bpsOf(t.rate) })),
      origination_fee_bps: bpsOf(origPct), origination_fee_fixed_minor: money(origFixed),
      late_fee_fixed_minor: money(lateFixed), late_fee_bps: bpsOf(latePct), grace_days: Number(grace), default_after_missed: Number(defaultAfter),
      partial_collection: partial, score_source: source, external_score_weight_bps: bpsOf(weight), max_active_loans: Number(maxActive),
      score_bands: bands.map((b) => ({ min_score: Number(b.score), max_principal_minor: money(b.max), rate_adjust_bps: bpsOf(b.adjust) })),
    };
    const clear: NonNullable<LoanProductInput["clear"]> = [];
    if (afford.trim()) body.max_installment_income_bps = bpsOf(afford); else if (product) clear.push("max_installment_income_bps");
    if (firstMax.trim()) body.first_loan_max_minor = money(firstMax); else if (product) clear.push("first_loan_max_minor");
    if (clear.length) body.clear = clear;
    if (product) return api.updateLoanProduct(bank.id, product.id, { ...body, status: active ? "active" : "inactive" });
    return api.createLoanProduct(bank.id, { ...body, code: toCode(name) });
  });
  const sym = cur.symbol ?? cur.code;
  const period = { daily: 1, weekly: 7, monthly: 30 }[freq];

  return (
    <Modal open size="wide" title={product ? `Edit ${product.name}` : "Add a loan product"} onClose={onClose}
      description={<>Amounts in {cur.code}; rates are annual. Time runs on the game clock.</>}
      onSubmit={async (e) => { e.preventDefault(); const p = await save.run(); if (p) onSaved(p); }}
      footer={<><Button onClick={onClose}>Cancel</Button><Button type="submit" variant="primary" busy={save.pending} disabled={!name.trim()}>{product ? "Save" : "Add product"}</Button></>}>
      <div className="form">
        <section className="form-section">
          <h3>Product</h3>
          <Field label="Name" hint={product ? undefined : name ? <>Code <span className="mono">{toCode(name)}</span></> : "Shown to players, e.g. Bus Finance."}>
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} required autoFocus placeholder="Bus Finance" />
          </Field>
          <div className="form-row">
            <Field label="Smallest loan" tip="The least a player can borrow on this product."><Affix before={sym}><Input inputMode="decimal" value={minP} onChange={(e) => setMinP(e.target.value)} required /></Affix></Field>
            <Field label="Largest loan" tip="The most anyone can borrow, even with a top score. Score bands below can lower it for each player."><Affix before={sym}><Input inputMode="decimal" value={maxP} onChange={(e) => setMaxP(e.target.value)} required /></Affix></Field>
            <Field label="Active loans per player" tip="How many loans of this product one player can have open at once. Paying one off frees the slot."><Input type="number" min={1} max={20} value={maxActive} onChange={(e) => setMaxActive(e.target.value)} /></Field>
          </div>
        </section>

        <section className="form-section">
          <h3>Repayment <InfoTip>Instalments fall due at the end of each period and are collected automatically from the player's account.</InfoTip></h3>
          <div className="form-row">
            <div className="field">
              <span>Instalments</span>
              <Segmented label="Frequency" value={freq} onChange={setFreq} options={[{ value: "daily", label: "Daily" }, { value: "weekly", label: "Weekly" }, { value: "monthly", label: "Monthly" }]} />
            </div>
            <div className="field">
              <span>Interest<InfoTip>Amortized: equal instalments, interest on what's left (like a car loan). Flat: interest on the original amount, split evenly.</InfoTip></span>
              <Segmented label="Interest method" value={method} onChange={setMethod} options={[{ value: "amortized", label: "Amortized" }, { value: "flat", label: "Flat" }]} />
            </div>
          </div>
          <div className="stack" style={{ gap: 8 }}>
            {terms.map((t, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) auto", gap: 8, alignItems: "end" }}>
                <Field label={i === 0 ? "Term" : ""}
                  tip={i === 0 ? "How long the player has to repay, in game days. Longer terms mean smaller instalments but more interest in total." : undefined}>
                  <Affix after="game days"><Input type="number" min={period} step={period} value={t.days} onChange={(e) => setTerms(terms.map((x, j) => (j === i ? { ...x, days: e.target.value } : x)))} required /></Affix>
                </Field>
                <Field label={i === 0 ? "Annual rate" : ""} tip={i === 0 ? "A yearly rate, charged per instalment period: 24% a year is about 0.46% a week. Score bands can raise or lower it for each player." : undefined}>
                  <Affix after="%"><Input inputMode="decimal" value={t.rate} onChange={(e) => setTerms(terms.map((x, j) => (j === i ? { ...x, rate: e.target.value } : x)))} required /></Affix>
                </Field>
                {terms.length > 1 ? <Button size="small" onClick={() => setTerms(terms.filter((_, j) => j !== i))} style={{ marginBottom: 6 }}>Remove</Button> : <span />}
              </div>
            ))}
            <small className="hint">Each term must be a multiple of {period} day{period === 1 ? "" : "s"}, the repayment period.</small>
            <div><Button size="small" onClick={() => setTerms([...terms, { days: "", rate: "" }])}>Add term</Button></div>
          </div>
        </section>

        <section className="form-section">
          <h3>Fees and missed payments</h3>
          <div className="form-row">
            <Field label="Opening fee" tip="Taken from the money paid out, to the bank's fee income.">
              <Affix after="%"><Input inputMode="decimal" value={origPct} onChange={(e) => setOrigPct(e.target.value)} placeholder="0" /></Affix>
            </Field>
            <Field label="+ fixed" tip="A flat amount on top of the percentage, e.g. a ₦500 processing fee."><Affix before={sym}><Input inputMode="decimal" value={origFixed} onChange={(e) => setOrigFixed(e.target.value)} placeholder="0" /></Affix></Field>
            <Field label="Late fee" tip="Added to an instalment when it's missed: a fixed amount plus a share of the instalment.">
              <Affix before={sym}><Input inputMode="decimal" value={lateFixed} onChange={(e) => setLateFixed(e.target.value)} placeholder="0" /></Affix>
            </Field>
            <Field label="+ of the instalment" tip="A share of the missed instalment, added on top of the fixed late fee. 2% of a ₦10,000 instalment adds ₦200."><Affix after="%"><Input inputMode="decimal" value={latePct} onChange={(e) => setLatePct(e.target.value)} placeholder="0" /></Affix></Field>
          </div>
          <div className="form-row">
            <Field label="Grace days" tip="Days after the due date before an unpaid instalment counts as missed.">
              <Input type="number" min={0} max={30} value={grace} onChange={(e) => setGrace(e.target.value)} />
            </Field>
            <Field label="Default after" tip="Missed instalments before the loan defaults: what's still owed is written off and the player's score falls.">
              <Affix after="missed"><Input type="number" min={1} max={52} value={defaultAfter} onChange={(e) => setDefaultAfter(e.target.value)} /></Affix>
            </Field>
          </div>
          <label className="row" style={{ gap: 8 }}>
            <input type="checkbox" checked={partial} onChange={(e) => setPartial(e.target.checked)} />
            <span>Collect part of an instalment <InfoTip>On: players chip away at what they owe, which suits forgiving games. Off (stricter): an instalment is either paid in full or, after the grace days, missed.</InfoTip> <span className="cell-sub">When the account can't cover it, take what's there. Off: wait until it can pay in full.</span></span>
          </label>
        </section>

        <section className="form-section">
          <h3>Who may borrow <InfoTip>The engine scores every player 0–100 from their money history: repayments, regular income, balances, account age, declined payments and savings.</InfoTip></h3>
          <div className="form-row">
            <Field label="Credit score from" tip="Engine: works in any game with no setup. Your game's own score: send external_score (0–100) with each quote and loan, e.g. a reputation stat. Blend: a weighted mix of both.">
              <Select value={source} onChange={(e) => setSource(e.target.value as ScoreSource)}>
                <option value="engine">The engine's credit score</option>
                <option value="game">Your game's own score (sent with each loan)</option>
                <option value="blend">A blend of both</option>
              </Select>
            </Field>
            {source === "blend" && (
              <Field label="Your game's share" tip="How much your game's score counts. 50% is an equal mix with the engine's score."><Affix after="%"><Input inputMode="decimal" value={weight} onChange={(e) => setWeight(e.target.value)} /></Affix></Field>
            )}
          </div>
          <div className="stack" style={{ gap: 8 }}>
            {bands.map((b, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "minmax(0, 0.7fr) minmax(0, 1fr) minmax(0, 0.8fr) auto", gap: 8, alignItems: "end" }}>
                <Field label={i === 0 ? "From score" : ""} tip={i === 0 ? "A band applies from this score up to where the next band starts. The highest band at or below a player's score is used." : undefined}><Input type="number" min={0} max={100} value={b.score} onChange={(e) => setBands(bands.map((x, j) => (j === i ? { ...x, score: e.target.value } : x)))} required /></Field>
                <Field label={i === 0 ? "Can borrow up to" : ""}
                  tip={i === 0 ? "The most a player in this band may borrow. Set 0 to refuse a band, e.g. scores below 40." : undefined}>
                  <Affix before={sym}><Input inputMode="decimal" value={b.max} onChange={(e) => setBands(bands.map((x, j) => (j === i ? { ...x, max: e.target.value } : x)))} required /></Affix>
                </Field>
                <Field label={i === 0 ? "Rate change" : ""} tip={i === 0 ? "Added to the term's rate for this band. Negative rewards good scores: -3% turns 24% into 21%." : undefined}><Affix after="%"><Input inputMode="decimal" value={b.adjust} onChange={(e) => setBands(bands.map((x, j) => (j === i ? { ...x, adjust: e.target.value } : x)))} placeholder="0" /></Affix></Field>
                {bands.length > 1 ? <Button size="small" onClick={() => setBands(bands.filter((_, j) => j !== i))} style={{ marginBottom: 6 }}>Remove</Button> : <span />}
              </div>
            ))}
            <small className="hint">A band that can borrow up to 0 refuses players in it.</small>
            <div><Button size="small" onClick={() => setBands([...bands, { score: "", max: "", adjust: "0" }])}>Add band</Button></div>
          </div>
          <div className="form-row">
            <Field label="Affordability" hint="Instalments at most this share of the player's income over the last 4 game weeks. Empty: no check."
              tip="Stops over-borrowing. With 40%, a player earning ₦100,000 a week can't take a loan whose weekly instalment, plus their other loans', is over ₦40,000.">
              <Affix after="% of income"><Input inputMode="decimal" value={afford} onChange={(e) => setAfford(e.target.value)} placeholder="None" /></Affix>
            </Field>
            <Field label="First loan up to" hint="Players with no loans yet start small. Empty: no cap."
              tip="New borrowers have no repayment history. A small first loan repaid on time raises their score and unlocks bigger ones.">
              <Affix before={sym}><Input inputMode="decimal" value={firstMax} onChange={(e) => setFirstMax(e.target.value)} placeholder="None" /></Affix>
            </Field>
          </div>
        </section>
        {product && (
          <label className="row" style={{ gap: 8 }}>
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            <span>Active <span className="cell-sub">An inactive product makes no new loans; existing ones keep collecting.</span></span>
          </label>
        )}
        <ErrorNotice error={save.error} />
      </div>
    </Modal>
  );
}

function LoanBook({ api, gameId, env, bank }: { api: GameApi; gameId: string; env: Env; bank: Bank }) {
  const [status, setStatus] = useState<"" | Loan["status"]>("");
  const loans = useQuery(`dev:loans:${gameId}:${env}:${bank.id}:${status}`, () => api.loans({ bank_id: bank.id, status: status || undefined, limit: 100 }), { refreshMs: 30_000 });
  const [open, setOpen] = useState<Loan | null>(null);
  const rows = loans.data?.data ?? [];
  const cur = bank.base_currency;
  return (
    <Panel title="Loans" sub="Newest first. Click a loan for its schedule." flush
      actions={<Segmented label="Status" value={status} onChange={setStatus}
        options={[{ value: "", label: "All" }, { value: "active", label: "Active" }, { value: "paid_off", label: "Paid off" }, { value: "defaulted", label: "Defaulted" }]} />}>
      <ErrorNotice error={loans.error} onRetry={loans.reload} />
      {loans.loading && !loans.data && <Loading />}
      {loans.data && rows.length === 0 && <p className="muted panel-body">No loans yet. Try one in the Sandbox playground: select a player's current account and choose Borrow.</p>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="data">
            <thead><tr>
              <th>Loan</th>
              <th className="num">Borrowed</th>
              <th className="num">Left to pay <InfoTip>Everything still owed: unpaid principal, interest and late fees. "Overdue" is the part already past due.</InfoTip></th>
              <th>Next due <InfoTip>The next instalment's game day and amount. It's collected automatically from the player's account.</InfoTip></th>
              <th>Score <InfoTip>The credit score this loan was priced on, and where it came from: the engine, your game, or a blend.</InfoTip></th>
              <th>Status <InfoTip>In arrears: an instalment was missed and is still owed. Defaulted: too many misses, so what was left was written off.</InfoTip></th>
            </tr></thead>
            <tbody>
              {rows.map((l) => (
                <tr key={l.id} className="clickable" onClick={() => setOpen(l)}>
                  <td><div className="cell-title">{l.product_name}</div><div className="cell-sub"><span className="acct-no">{l.account_number}</span> · {l.start_day}</div></td>
                  <td className="num"><Money minor={l.principal_minor} currency={cur} /><div className="cell-sub">{pct(l.rate_bps)} · {l.term_days} days</div></td>
                  <td className="num">
                    <Money minor={l.remaining_minor} currency={cur} />
                    {l.arrears_minor > 0 && <div className="cell-sub" style={{ color: "var(--debit)" }}>{formatMoney(l.arrears_minor, cur)} overdue</div>}
                  </td>
                  <td>{l.status === "active" && l.next_due_day ? <>{l.next_due_day}<div className="cell-sub">{formatMoney(l.next_due_minor, cur)}</div></> : <span className="muted">—</span>}</td>
                  <td>{l.score_used}<div className="cell-sub">{l.score_source}</div></td>
                  <td>{loanStatusBadge(l)}{l.missed_count > 0 && <div className="cell-sub">{l.missed_count} missed</div>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {open && <LoanDetail api={api} gameId={gameId} env={env} loan={open} cur={cur} onClose={() => setOpen(null)} />}
    </Panel>
  );
}

function LoanDetail({ api, gameId, env, loan, cur, onClose }: { api: GameApi; gameId: string; env: Env; loan: Loan; cur: CurrencyLike; onClose: () => void }) {
  const q = useQuery(`dev:loan:${gameId}:${env}:${loan.id}`, () => api.loan(loan.id));
  const l = q.data ?? loan;
  return (
    <Modal open size="wide" title={`${l.product_name}: ${formatMoney(l.principal_minor, cur)}`} onClose={onClose} footer={<Button onClick={onClose}>Close</Button>}
      description={<>Loan account <span className="acct-no">{l.account_number}</span> · {pct(l.rate_bps)} a year · {l.installments_count} {describeFrequency(l.repayment_frequency)} · score {l.score_used} ({l.score_source})</>}>
      <ErrorNotice error={q.error} />
      <dl className="facts" style={{ marginBottom: 16 }}>
        <dt>Status</dt><dd>{loanStatusBadge(l)}</dd>
        <dt>Principal still owed <InfoTip>The borrowed amount not yet repaid. Interest and late fees aren't included.</InfoTip></dt><dd>{formatMoney(l.outstanding_principal_minor, cur)}</dd>
        <dt>Paid so far</dt><dd>{formatMoney(l.principal_paid_minor + l.interest_paid_minor + l.late_fees_paid_minor, cur)} <span className="cell-sub">({formatMoney(l.interest_paid_minor, cur)} interest, {formatMoney(l.late_fees_paid_minor, cur)} late fees)</span></dd>
        {l.origination_fee_minor > 0 && <><dt>Opening fee <InfoTip>Taken from the loan when it was paid out, to the bank's fee income.</InfoTip></dt><dd>{formatMoney(l.origination_fee_minor, cur)}</dd></>}
        {l.written_off_minor > 0 && <><dt>Written off <InfoTip>What was still owed when the loan defaulted or was forgiven, taken from the bank's loan-loss provision.</InfoTip></dt><dd>{formatMoney(l.written_off_minor, cur)}</dd></>}
      </dl>
      {l.installments && (
        <div className="table-wrap">
          <table className="data compact">
            <thead><tr><th>#</th><th>Due</th><th className="num">Principal</th><th className="num">Interest</th><th className="num">Late fee</th><th className="num">Paid</th><th className="num">Owed</th><th>Status</th></tr></thead>
            <tbody>
              {l.installments.map((i) => (
                <tr key={i.seq}>
                  <td>{i.seq}</td><td>{i.due_day}</td>
                  <td className="num">{formatMoney(i.principal_due_minor, cur)}</td>
                  <td className="num">{formatMoney(i.interest_due_minor, cur)}</td>
                  <td className="num">{i.late_fee_due_minor ? formatMoney(i.late_fee_due_minor, cur) : "—"}</td>
                  <td className="num">{formatMoney(i.paid_minor, cur)}</td>
                  <td className="num">{i.owed_minor ? formatMoney(i.owed_minor, cur) : "—"}</td>
                  <td><span className={`badge ${i.status === "paid" ? "ok" : i.status === "missed" ? "bad" : "plain"}`}>{i.status}{i.missed && i.status === "paid" ? " late" : ""}</span>{i.paid_day && <div className="cell-sub">{i.paid_day}</div>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
